import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X, User } from 'lucide-react';
import { formatCurrency } from '../utils/currency';

const TeamMemberSearchSelect = ({ 
  members, 
  selectedMemberId, 
  onSelect, 
  placeholder = "Search for a team member...",
  disabled = false,
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selectedMember = members.find(m => m.id === selectedMemberId);

  const filteredMembers = useMemo(() => {
    if (searchTerm.trim() === '') {
      return members.slice(0, 10);
    }
    const term = searchTerm.toLowerCase();
    const filtered = members.filter(member => 
      member.name?.toLowerCase().includes(term) ||
      member.role?.toLowerCase().includes(term) ||
      member.employee_id?.toLowerCase().includes(term)
    );
    return filtered.slice(0, 20);
  }, [searchTerm, members]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (member) => {
    onSelect(member.id);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onSelect('');
    setSearchTerm('');
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {selectedMember && !isOpen ? (
        <div 
          className={`flex items-center justify-between w-full px-4 py-2.5 border border-slate-200 rounded-lg bg-white cursor-pointer hover:border-slate-300 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
          onClick={() => !disabled && setIsOpen(true)}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <div className="min-w-0">
              <span className="text-slate-900 truncate block">
                {selectedMember.employee_id ? `${selectedMember.employee_id} – ${selectedMember.name}` : selectedMember.name}
              </span>
              <span className="text-xs text-slate-500 truncate block">
                {selectedMember.role}
              </span>
            </div>
          </div>
          {!disabled && (
            <button onClick={handleClear} className="p-1 hover:bg-slate-100 rounded ml-2 flex-shrink-0">
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => !disabled && setIsOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            className={`w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${disabled ? 'bg-slate-50 opacity-60 cursor-not-allowed' : ''}`}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
      )}

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {filteredMembers.length > 0 ? (
            <ul className="py-1">
              {filteredMembers.map((member) => (
                <li
                  key={member.id}
                  onClick={() => handleSelect(member)}
                  className={`px-4 py-2.5 cursor-pointer hover:bg-slate-50 ${member.id === selectedMemberId ? 'bg-blue-50' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-900">
                          {member.employee_id ? `${member.employee_id} – ${member.name}` : member.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                        <span>{member.role}</span>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                      member.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                    }`}>
                      {member.status}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-6 text-center text-slate-500">
              <Search className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No team members found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamMemberSearchSelect;
